import {useState, useCallback} from 'react';
import {
    View,
    Text,
    StyleSheet,
    Modal,
    TouchableOpacity,
    TextInput,
    Platform,
    Alert,
} from 'react-native';
import {Upload, FolderPlus, Check, X} from 'lucide-react-native';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';

interface SharedFolderActionSheetProps {
    visible: boolean;
    onClose: () => void;
    onUpload: () => void;
    onCreateFolder: (name: string) => Promise<void>;
}

export const SharedFolderActionSheet = ({
    visible,
    onClose,
    onUpload,
    onCreateFolder,
}: SharedFolderActionSheetProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    const [mode, setMode] = useState<'menu' | 'folderInput'>('menu');
    const [folderName, setFolderName] = useState('');
    const [creating, setCreating] = useState(false);

    const handleClose = useCallback(() => {
        setMode('menu');
        setFolderName('');
        onClose();
    }, [onClose]);

    const handleUpload = useCallback(() => {
        onUpload();
        handleClose();
    }, [onUpload, handleClose]);

    const handleCreateFolder = useCallback(async () => {
        if (!folderName.trim()) return;
        setCreating(true);
        try {
            await onCreateFolder(folderName.trim());
            setFolderName('');
            setMode('menu');
            onClose();
        } catch (e: any) {
            if (Platform.OS === 'web') {
                console.error('Failed to create folder:', e);
            } else {
                Alert.alert(t('common.error'), e.message || t('common.unknownError'));
            }
        } finally {
            setCreating(false);
        }
    }, [folderName, onCreateFolder, onClose, t]);

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="slide"
            onRequestClose={handleClose}
        >
            <TouchableOpacity
                style={styles.overlay}
                activeOpacity={1}
                onPress={handleClose}
            >
                <TouchableOpacity activeOpacity={1}>
                    <View style={styles.sheet}>
                        <View style={styles.dragIndicator} />

                        {mode === 'menu' ? (
                            <View style={styles.actionList}>
                                <TouchableOpacity
                                    style={styles.actionRow}
                                    onPress={handleUpload}
                                >
                                    <View style={styles.actionIcon}>
                                        <Upload size={22} color={theme.colors.primary} />
                                    </View>
                                    <View style={styles.actionTextContainer}>
                                        <Text style={styles.actionTitle}>
                                            {t('media.sharedFolders.upload')}
                                        </Text>
                                        <Text style={styles.actionSubtitle}>
                                            {t('media.sharedFolders.uploadSubtitle')}
                                        </Text>
                                    </View>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={styles.actionRow}
                                    onPress={() => setMode('folderInput')}
                                >
                                    <View style={styles.actionIcon}>
                                        <FolderPlus size={22} color={theme.colors.primary} />
                                    </View>
                                    <View style={styles.actionTextContainer}>
                                        <Text style={styles.actionTitle}>
                                            {t('media.sharedFolders.newFolder')}
                                        </Text>
                                        <Text style={styles.actionSubtitle}>
                                            {t('media.sharedFolders.newFolderSubtitle')}
                                        </Text>
                                    </View>
                                </TouchableOpacity>
                            </View>
                        ) : (
                            <View style={styles.folderInputContainer}>
                                <Text style={styles.folderInputTitle}>
                                    {t('media.sharedFolders.newFolder')}
                                </Text>
                                <View style={styles.inputRow}>
                                    <TextInput
                                        style={styles.input}
                                        value={folderName}
                                        onChangeText={setFolderName}
                                        placeholder={t('media.sharedFolders.folderNamePlaceholder')}
                                        placeholderTextColor={theme.colors.textSecondary}
                                        autoFocus
                                        editable={!creating}
                                        onSubmitEditing={handleCreateFolder}
                                    />
                                    <TouchableOpacity
                                        style={[styles.iconButton, styles.confirmButton]}
                                        onPress={handleCreateFolder}
                                        disabled={creating || !folderName.trim()}
                                    >
                                        <Check size={20} color="white" />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={styles.iconButton}
                                        onPress={() => {
                                            setMode('menu');
                                            setFolderName('');
                                        }}
                                        disabled={creating}
                                    >
                                        <X size={20} color={theme.colors.textSecondary} />
                                    </TouchableOpacity>
                                </View>
                            </View>
                        )}
                    </View>
                </TouchableOpacity>
            </TouchableOpacity>
        </Modal>
    );
};

const createStyles = (theme: ThemeType, insets: {bottom: number}) =>
    StyleSheet.create({
        overlay: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            justifyContent: 'flex-end',
        },
        sheet: {
            backgroundColor: theme.colors.background,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingTop: 12,
            paddingBottom: insets.bottom + 20,
        },
        dragIndicator: {
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.colors.border,
            alignSelf: 'center',
            marginBottom: 16,
        },
        actionList: {
            paddingHorizontal: 20,
            paddingVertical: 8,
            gap: 12,
        },
        actionRow: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            borderRadius: 12,
            padding: 16,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        actionIcon: {
            width: 44,
            height: 44,
            borderRadius: 22,
            backgroundColor: theme.colors.primary + '15',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 16,
        },
        actionTextContainer: {
            flex: 1,
        },
        actionTitle: {
            fontSize: 16,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: 2,
        },
        actionSubtitle: {
            fontSize: 13,
            color: theme.colors.textSecondary,
        },
        folderInputContainer: {
            paddingHorizontal: 20,
            paddingVertical: 16,
        },
        folderInputTitle: {
            fontSize: 18,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: 16,
        },
        inputRow: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
        },
        input: {
            flex: 1,
            borderWidth: 1,
            borderColor: theme.colors.border,
            borderRadius: theme.borderRadius.md,
            paddingHorizontal: theme.spacing.sm,
            paddingVertical: theme.spacing.sm,
            fontSize: theme.fontSize.sm,
            color: theme.colors.text,
            backgroundColor: theme.colors.surface,
        },
        iconButton: {
            padding: 10,
            borderRadius: theme.borderRadius.sm,
        },
        confirmButton: {
            backgroundColor: theme.colors.primary,
            borderRadius: theme.borderRadius.md,
        },
    });
